// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigationType } from 'react-router-dom';
import Home from './pages/Home';
import Service from '../src/pages/Services';
import AboutUs from '../src/pages/AboutUs';
import Contact from './pages/Contact';
import FlightBooking from './pages/services/FlightBooking';
import CarRental from './pages/services/CarRental';
import HajjUmrah from './pages/services/HajjUmrah';
import Cruise from './pages/services/Cruise';
import TourPackages from './pages/services/TourPackages';
import TravelInsurance from './pages/services/TravelInsurance';
import PassportVisa from './pages/services/PassportVisa';
import Forex from './pages/services/Forex';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import WhatsAppFloat from './components/WhatsAppFloat';
import LoadingAnimation from './components/LoadingAnimation';
import WelcomePopup from './components/WelcomePopup';
import { AnimatePresence } from 'framer-motion';

// Custom component to handle page transitions
const PageTransitionHandler = ({ children }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [content, setContent] = useState(null);
    const [prevPathname, setPrevPathname] = useState('');
    const location = useLocation();
    const loadingTimerRef = useRef(null);
    const forceHideTimerRef = useRef(null);

    // Store the children in state to prevent flash of content
    useEffect(() => {
        // Always update content when children change
        setContent(children);
    }, [children]);

    // Manage scrolling based on loading state
    useEffect(() => {
        if (!isLoading) {
            // Enable scrolling when content is displayed
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
            document.body.style.height = 'auto';
            document.documentElement.style.height = 'auto';
        } else {
            // Disable scrolling during loading
            document.body.style.overflow = 'hidden';
            document.documentElement.style.overflow = 'hidden';
            document.body.style.height = '100%';
            document.documentElement.style.height = '100%';
        }
    }, [isLoading]);

    // Force hide loading after a maximum time to prevent stuck loading
    useEffect(() => {
        if (isLoading) {
            // Check network speed to determine maximum loading time
            const connection = navigator.connection ||
                               navigator.mozConnection ||
                               navigator.webkitConnection;

            let maxLoadingTime = 2000; // 2 seconds for good internet (1s longer than normal)

            if (connection) {
                // If we have the Network Information API
                if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
                    maxLoadingTime = 6000; // Very slow connection - longer maximum time
                } else if (connection.effectiveType === '3g') {
                    maxLoadingTime = 4000; // Medium connection
                }
            }

            forceHideTimerRef.current = setTimeout(() => {
                console.log('Force hiding loading animation after max time:', maxLoadingTime);
                setIsLoading(false);

                // Force enable scrolling
                document.body.style.overflow = 'auto';
                document.documentElement.style.overflow = 'auto';
                document.body.style.height = 'auto';
                document.documentElement.style.height = 'auto';
            }, maxLoadingTime);
        }

        return () => {
            if (forceHideTimerRef.current) {
                clearTimeout(forceHideTimerRef.current);
            }
        };
    }, [isLoading]);

    // Handle page transitions
    useEffect(() => {
        // Only trigger loading animation if the pathname has changed
        if (location.pathname !== prevPathname) {
            console.log('Path changed from', prevPathname, 'to', location.pathname);

            // Clear any existing timers
            if (loadingTimerRef.current) {
                clearTimeout(loadingTimerRef.current);
            }

            // Show loading animation on location change
            setIsLoading(true);
            setPrevPathname(location.pathname);

            // Scroll to top immediately but hidden by the loading overlay
            window.scrollTo(0, 0);

            // Check network speed to determine loading time
            const connection = navigator.connection ||
                               navigator.mozConnection ||
                               navigator.webkitConnection;

            let loadingTime = 1000; // Exactly 1 second for good internet

            if (connection) {
                // If we have the Network Information API
                if (connection.effectiveType === '2g' || connection.effectiveType === 'slow-2g') {
                    loadingTime = 4000; // Slow connection - longer loading time
                } else if (connection.effectiveType === '3g') {
                    loadingTime = 2500; // Medium connection - slightly longer loading time
                }
                console.log('Connection type:', connection.effectiveType, 'Loading time:', loadingTime);
            } else {
                // Fallback method: check how fast a small resource loads
                const startTime = Date.now();
                const img = new Image();
                img.onload = () => {
                    const loadTime = Date.now() - startTime;
                    if (loadTime > 100) {
                        // If it takes more than 100ms to load a tiny image, connection might be slow
                        loadingTime = 3000; // Slower connection
                    }
                };
                img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'; // 1x1 transparent gif
            }

            // Hide loading animation after the determined delay
            loadingTimerRef.current = setTimeout(() => {
                console.log('Setting isLoading to false, loading time:', loadingTime);
                setIsLoading(false);
            }, loadingTime);
        }

        // Clean up timer if component unmounts
        return () => {
            if (loadingTimerRef.current) {
                console.log('Clearing timeout');
                clearTimeout(loadingTimerRef.current);
            }
        };
    }, [location.pathname, prevPathname]);

    return (
        <>
            <LoadingAnimation isLoading={isLoading} />
            {/* Always render content, but it will be hidden by the loading overlay when isLoading is true */}
            {content}
        </>
    );
};

function App() {
    return (
        <Router>
            <PageTransitionHandler>
                <Navbar />
                <AnimatePresence>
                    <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/services" element={<Service />} />
                        <Route path="/AboutUs" element={<AboutUs />} />
                        <Route path="/contact" element={<Contact />} />
                        <Route path="/services/flight-booking" element={<FlightBooking />} />
                        <Route path="/services/car-rental" element={<CarRental />} />
                        <Route path="/services/hajj-umrah" element={<HajjUmrah />} />
                        <Route path="/services/cruise" element={<Cruise />} />
                        <Route path="/services/tour-packages" element={<TourPackages />} />
                        <Route path="/services/travel-insurance" element={<TravelInsurance />} />
                        <Route path="/services/passport-visa" element={<PassportVisa />} />
                        <Route path="/services/forex" element={<Forex />} />
                    </Routes>
                </AnimatePresence>
                <WelcomePopup />
                <WhatsAppFloat />
                <Footer />
            </PageTransitionHandler>
        </Router>
    );
}

export default App;
